import * as yup from "yup";
import { FlowType } from "../../context/use-booking-flow/use-booking-flow.types";
import {
  hasMaxDecimals,
  isDecimalString,
} from "@/utils/format/regex/just-decimal";

// months / take / members accept only a plain string of digits — matching what
// the form's JustNumber (strips everything but [0-9]) produces. Number()-based
// checks are too loose: they'd let "Infinity", "1e3", "0x1f" and whitespace-only
// strings through, which would then be sent verbatim in the booking payload.
// credits is the exception: it is fractional (see below) and uses the JustDecimal
// predicates, which reject those same values.
const isNumericString = (value?: string) =>
  value !== undefined && /^\d+$/.test(value);

export const CreateBookingSchema = yup.object().shape({
  company: yup
    .object()
    .shape({
      id: yup.number().required(""),
      name: yup.string().required(""),
    })
    .default(undefined)
    .required("Company obrigatória"),
  package: yup
    .object()
    .shape({
      id: yup.number().required(""),
      name: yup.string().required(""),
    })
    .default(undefined)
    .required("Pacote obrigatório"),
  coworking: yup
    .object()
    .shape({
      id: yup.number().required(""),
      name: yup.string().required(""),
    })
    .default(undefined)
    .required("Coworking obrigatório"),
  private_room: yup
    .object()
    .shape({
      id: yup.number().required(""),
      name: yup.string().required(""),
    })
    .default(undefined)
    .required("Private Room obrigatório"),
  start_date: yup.string().required("Data obrigatória"),
  // These fields stay `string` on purpose: the form uses text inputs and the
  // booking payload (CreateBookingDataParams) sends them as strings. Switching to
  // yup.number() would change the wire contract, since yupResolver returns cast
  // values. We keep the string type and validate the numeric VALUE via .test()
  // (plain .min/.max on a string would validate its char LENGTH, not its value).
  months: yup
    .string()
    .required("Meses obrigatório")
    .test("is-number", "Meses deve ser um número", isNumericString)
    .test("min", "O valor mínimo é 1", (value) => Number(value) >= 1),
  // credits accepts up to 2 decimals: the PO package/contract already prices
  // credits fractionally (monthlyCredits is decimal(10,2) in the backend), so
  // forcing an integer here changed the booked value. The form mask (JustDecimal)
  // already truncates at 2 decimals; "max-decimals" guards pasted/programmatic
  // values.
  //
  // No upper bound on credits: the value is auto-computed by CalcCreditsToConsume
  // (roomMonthlyPrice / creditPrice) and legitimately exceeds 80 for higher-priced
  // rooms. A numeric max would block those bookings. (The prior string .max(80)
  // was effectively a no-op — it capped the char count, never the value.)
  credits: yup
    .string()
    .required("Créditos obrigatório")
    .test(
      "is-number",
      "Créditos deve ser um número",
      (value) => value !== undefined && isDecimalString(value),
    )
    .test(
      "max-decimals",
      "Créditos deve ter no máximo 2 casas decimais",
      (value) => value !== undefined && hasMaxDecimals(value),
    )
    .test("min", "O valor mínimo é 1", (value) => Number(value) >= 1),
  take: yup
    .string()
    .required("Take obrigatório")
    .test("is-number", "Take deve ser um número", isNumericString)
    .test("min", "O valor mínimo é 1", (value) => Number(value) >= 1),
  members: yup
    .string()
    .required("Membros obrigatório")
    .test("is-number", "Membros deve ser um número", isNumericString)
    .test("min", "O valor mínimo é 1", (value) => Number(value) >= 1),
  flow_type: yup
    .string()
    .oneOf(["default", "upsell"] as FlowType[], "Tipo de fluxo inválido")
    .required("Flow type obrigatório"),
});
